// packages/editor/src/studio/SourceEditor.tsx
//
// Plain <textarea> source editor with sibling line-number column +
// cursor-line tracking. When the cursor is on a line that contains a
// slot directive, onSlotFocus is called with the parsed SlotConfig.
//
// E2 MVP — no syntax highlighting, no autocomplete. CodeMirror is
// overkill for source-mode authoring; revisit if friction emerges.

import { useEffect, useRef, useState } from 'react';
import { parseToMdast } from '../round-trip/round-trip';
import { normalizeAgentMarkdown } from '@rmint/core/directives/normalize-agent-markdown';
import { parseSlot } from '../slot-grammar/parse-slot';
import type { SlotConfig } from '../slot-grammar/slot-types';

export interface SourceEditorProps {
  value: string;
  onChange: (next: string) => void;
  onBlur: () => void;
  /** Fired when the cursor lands on a line containing a :::slot[...]::: */
  onSlotFocus: (cfg: SlotConfig | null) => void;
}

const SLOT_LINE_RE = /:::slot\[/;

export function SourceEditor({ value, onChange, onBlur, onSlotFocus }: SourceEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [cursorLine, setCursorLine] = useState<number>(0);

  const lineCount = value.split('\n').length;
  const lineNums = Array.from({ length: lineCount }, (_, i) => i + 1).join('\n');

  const updateCursorLine = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const upToCursor = value.slice(0, ta.selectionStart);
    setCursorLine(upToCursor.split('\n').length - 1);
  };

  useEffect(() => {
    const lines = value.split('\n');
    const line = lines[cursorLine] ?? '';
    if (!SLOT_LINE_RE.test(line)) {
      onSlotFocus(null);
      return;
    }
    try {
      const normalized = normalizeAgentMarkdown(line);
      const tree: any = parseToMdast(normalized);
      const wrap = tree.children[0];
      const directive = wrap?.type === 'paragraph' ? wrap.children[0] : wrap;
      const cfg = parseSlot(directive);
      onSlotFocus(cfg);
    } catch {
      onSlotFocus(null);
    }
  }, [cursorLine, value, onSlotFocus]);

  return (
    <div
      data-testid="source-editor"
      style={{
        display: 'flex',
        height: '100%',
        fontFamily: 'monospace',
        fontSize: 13,
        background: '#fafafa',
      }}
    >
      <pre
        data-testid="source-line-nums"
        style={{
          margin: 0,
          padding: '8px 6px',
          color: '#9ca3af',
          textAlign: 'right',
          background: '#f3f4f6',
          borderRight: '1px solid #e5e7eb',
          userSelect: 'none',
          minWidth: 36,
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        {lineNums}
      </pre>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onSelect={updateCursorLine}
        onKeyUp={updateCursorLine}
        onClick={updateCursorLine}
        spellCheck={false}
        style={{
          flex: 1,
          padding: 8,
          border: 'none',
          outline: 'none',
          background: '#ffffff',
          fontFamily: 'monospace',
          fontSize: 13,
          lineHeight: 1.5,
          resize: 'none',
        }}
      />
    </div>
  );
}
